import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Space, Typography, Input, Switch, Alert, Button, App } from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { api } from '../api';
import { t } from '../i18n';

/** Base64-encode an ArrayBuffer in chunks — btoa can't reliably swallow a whole
 * MB-sized binary string in one call. */
export function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Superadmin modal to publish a new firmware release: pick the aarch64 binary,
 * enter a version, POST it base64-encoded to the publish endpoint. The server
 * hashes + signs it; devices self-apply on their next update poll. */
export function FirmwarePublishModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [mandatory, setMandatory] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const reset = (): void => {
    setVersion('');
    setNotes('');
    setMandatory(false);
    setFile(null);
  };

  const publish = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error(t('fwNoFile'));
      const binaryBase64 = arrayBufferToBase64(await file.arrayBuffer());
      return api.publishFirmware({
        // Optional: the server derives the version from the binary's embedded
        // marker. A typed value is sent only as an assertion (must match).
        version: version.trim() || undefined,
        binaryBase64,
        mandatory,
        releaseNotes: notes.trim() || undefined,
      });
    },
    onSuccess: (r) => {
      message.success(t('fwPublished', { version: r.version, size: r.size }));
      qc.invalidateQueries({ queryKey: ['firmware-releases'] });
      qc.invalidateQueries({ queryKey: ['devices'] });
      reset();
      onClose();
    },
    onError: (e) => message.error((e as Error).message),
  });

  return (
    <Modal
      open={open}
      title={t('fwPublishTitle')}
      okText={t('fwPublishOk')}
      onOk={() => publish.mutate()}
      confirmLoading={publish.isPending}
      okButtonProps={{ disabled: !file }}
      onCancel={() => {
        reset();
        onClose();
      }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary">{t('fwBinary')}</Typography.Text>
          <div style={{ marginTop: 4 }}>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
        </div>
        <div>
          <Typography.Text type="secondary">{t('fwVersion')}</Typography.Text>
          <Input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder={t('fwVersionAutoPlaceholder')}
          />
        </div>
        <Space>
          <Switch checked={mandatory} onChange={setMandatory} />
          <Typography.Text>{t('fwMandatory')}</Typography.Text>
        </Space>
        <div>
          <Typography.Text type="secondary">{t('fwNotes')}</Typography.Text>
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
        </div>
        <Alert type="info" showIcon message={t('fwHint')} />
      </Space>
    </Modal>
  );
}

/** A "Publish firmware" button that opens the publish modal. Superadmin-only —
 * gate at the call site. */
export function FirmwarePublishButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button icon={<CloudDownloadOutlined />} onClick={() => setOpen(true)}>
        {t('fwPublishBtn')}
      </Button>
      <FirmwarePublishModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
